import {
    SERVER_ADDRESS
} from "./restAPI/constants";
import {
    getHeaders
} from "./restAPI/utils";
import axios from "axios";
import {
    navigationRef
} from "../navigation";

interface IOneSignalWebUser {
    pushSubscription?: {
        optOut?: () => Promise<void>;
        optIn?: () => Promise<void>;
    };
    removeTag?: (tagKey: string) => Promise<void>;
    addTags?: (tags: Record<string, string>) => Promise<void>;
}

interface IOneSignalWebNotifications {
    requestPermission?: () => Promise<boolean>;
    addEventListener?: (
        event: "click",
        listener: (event: {
            notification?: {
                additionalData?: Record<string, unknown>;
            };
        }) => void
    ) => void;
    isPushSupported?: () => boolean;
}

interface IOneSignalWebInitOptions {
    allowLocalhostAsSecureOrigin?: boolean;
    serviceWorkerParam?: {
        scope?: string;
    };
    serviceWorkerPath?: string;
    safari_web_id?: string;
    notifyButton?: {
        enable?: boolean;
    };
    appId: string;
}

interface IOneSignalWeb {
    Notifications?: IOneSignalWebNotifications;
    logout?: () => Promise<void>;
    login?: (externalId: string) => Promise<void>;
    init?: (options: IOneSignalWebInitOptions) => Promise<void>;
    User?: IOneSignalWebUser;
}

declare global {
    interface Window {
        OneSignalDeferred?: Array<(oneSignal: IOneSignalWeb) => Promise<void> | void>;
        __isOneSignalInitialized?: boolean;
        OneSignal?: IOneSignalWeb;
    }
}

class OneSignalService {
    private static instance: OneSignalService;
    private readonly appId = "07d70a0f-8a43-4a7f-88c0-a99dc9cf6fdc";
    private isInitialized = false;

    private constructor() {}

    public static getInstance(): OneSignalService {
        if(!OneSignalService.instance) {
            OneSignalService.instance = new OneSignalService();
        }
        return OneSignalService.instance;
    }

    private withOneSignal(action: (oneSignal: IOneSignalWeb) => void): void {
        if(typeof window === "undefined") {
            return;
        }

        try {
            if(window.OneSignal) {
                action(window.OneSignal);
                return;
            }

            window.OneSignalDeferred = window.OneSignalDeferred || [];
            window.OneSignalDeferred.push((oneSignal: IOneSignalWeb) => {
                try {
                    action(oneSignal);
                } catch(error) {
                    console.warn("[OneSignal] Deferred action failed:", error);
                }
            });
        } catch(error) {
            console.warn("[OneSignal] withOneSignal failed:", error);
        }
    }

    public initialize(): void {
        if(this.isInitialized) {
            return;
        }
        this.isInitialized = true;

        this.withOneSignal((oneSignal: IOneSignalWeb) => {
            const setupListeners = () => {
                try {
                    if(typeof oneSignal?.Notifications?.addEventListener === "function") {
                        oneSignal.Notifications.addEventListener("click", (event) => {
                            const data = (event?.notification?.additionalData || {
                            }) as Record<string, unknown>;
                            const notificationId = typeof data.notificationId === "string"
                                ? data.notificationId
                                : typeof data.notificationID === "string"
                                    ? data.notificationID
                                    : undefined;

                            if(notificationId) {
                                axios(`${SERVER_ADDRESS}/notification/markAsRead`, {
                                    data: {
                                        notificationID: notificationId
                                    },
                                    headers: getHeaders(true),
                                    method: "POST"
                                }).catch(() => {
                                });
                            }

                            this.handleNotificationNavigation(data);
                        });
                    }

                    if(typeof oneSignal?.Notifications?.requestPermission === "function") {
                        oneSignal.Notifications.requestPermission().then((granted: boolean) => {
                            if(granted && typeof oneSignal?.User?.pushSubscription?.optIn === "function") {
                                oneSignal.User.pushSubscription.optIn().catch(() => {
                                });
                            }
                        }).catch(() => {
                        });
                    }
                } catch(error) {
                    console.warn("[OneSignal] setupListeners error:", error);
                }
            };

            if(typeof window !== "undefined" && window.__isOneSignalInitialized) {
                setupListeners();
                return;
            }

            if(typeof oneSignal?.init !== "function") {
                setupListeners();
                return;
            }

            oneSignal.init({
                safari_web_id: "web.onesignal.auto.69f4c26a-74c4-44a8-8df4-068c09b09538",
                serviceWorkerPath: "OneSignalSDKWorker.js",
                allowLocalhostAsSecureOrigin: true,
                serviceWorkerParam: {
                    scope: "/"
                },
                notifyButton: {
                    enable: true
                },
                appId: this.appId
            }).then(() => {
                if(typeof window !== "undefined") {
                    window.__isOneSignalInitialized = true;
                }
                setupListeners();
            }).catch(() => {
                setupListeners();
            });
        });
    }

    private handleNotificationNavigation(data: Record<string, unknown>): void {
        const navigate = () => {
            if(!navigationRef.isReady()) {
                return false;
            }

            if(typeof data.announcementID === "string") {
                navigationRef.navigate("Common", {
                    params: {
                        announcementID: data.announcementID
                    },
                    screen: "AnnouncementRouter"
                });
                return true;
            }

            if(typeof data.newsID === "string") {
                navigationRef.navigate("Common", {
                    params: {
                        newsID: data.newsID
                    },
                    screen: "NewsRouter"
                });
                return true;
            }

            if(typeof data.formID === "string") {
                navigationRef.navigate("Common", {
                    params: {
                        formID: data.formID
                    },
                    screen: "FormRouter"
                });
                return true;
            }

            if(typeof data.eventID === "string") {
                navigationRef.navigate("App", {
                    params: {
                        eventTitle: typeof data.title === "string" ? data.title : "",
                        eventID: data.eventID
                    },
                    screen: "EventDetail"
                });
                return true;
            }

            return true;
        };

        if(!navigate()) {
            const interval = setInterval(() => {
                if(navigate()) {
                    clearInterval(interval);
                }
            }, 150);

            setTimeout(() => {
                clearInterval(interval);
            }, 5000);
        }
    }

    public login(externalId: string): void {
        this.withOneSignal((oneSignal: IOneSignalWeb) => {
            try {
                if(typeof oneSignal?.login === "function") {
                    oneSignal.login(externalId).catch(() => {
                    });
                }

                if(typeof oneSignal?.User?.pushSubscription?.optIn === "function") {
                    oneSignal.User.pushSubscription.optIn().catch(() => {
                    });
                }

                if(typeof oneSignal?.Notifications?.requestPermission === "function") {
                    oneSignal.Notifications.requestPermission().then((granted: boolean) => {
                        if(granted && typeof oneSignal?.User?.pushSubscription?.optIn === "function") {
                            oneSignal.User.pushSubscription.optIn().catch(() => {
                            });
                        }
                    }).catch(() => {
                    });
                }
            } catch(error) {
                console.warn("[OneSignal] login error:", error);
            }
        });
    }

    public logout(): void {
        this.withOneSignal((oneSignal: IOneSignalWeb) => {
            try {
                if(typeof oneSignal?.User?.pushSubscription?.optOut === "function") {
                    oneSignal.User.pushSubscription.optOut().catch(() => {
                    });
                }

                if(typeof oneSignal?.User?.removeTag === "function") {
                    oneSignal.User.removeTag("userID").catch(() => {
                    });
                }

                if(typeof oneSignal?.logout === "function") {
                    oneSignal.logout().catch(() => {
                    });
                }
            } catch(error) {
                console.warn("[OneSignal] logout error:", error);
            }
        });
        this.clearAllNotifications();
    }

    public setTags(tags: Record<string, string>): void {
        this.withOneSignal((oneSignal: IOneSignalWeb) => {
            try {
                if(typeof oneSignal?.User?.addTags === "function") {
                    oneSignal.User.addTags(tags).catch(() => {
                    });
                }
            } catch(error) {
                console.warn("[OneSignal] setTags error:", error);
            }
        });
    }

    public removeTag(tagKey: string): void {
        this.withOneSignal((oneSignal: IOneSignalWeb) => {
            try {
                if(typeof oneSignal?.User?.removeTag === "function") {
                    oneSignal.User.removeTag(tagKey).catch(() => {
                    });
                }
            } catch(error) {
                console.warn("[OneSignal] removeTag error:", error);
            }
        });
    }

    public clearAllNotifications(): void {
        if(typeof window !== "undefined" && "serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((registration) => {
                    registration.getNotifications().then((notifications) => {
                        notifications.forEach((notification) => {
                            notification.close();
                        });
                    }).catch(() => {
                    });
                });
            }).catch(() => {
            });
        }
    }
}
export default OneSignalService.getInstance();
